import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { postOverlay, num, str } from '@/lib/overlayApi';

export interface Option {
  value: string;
  label: string;
  /** Sales in the probe window — used to order the list by what matters. */
  weight: number;
}

type Dim = 'category' | 'city' | 'sku';

/**
 * Options for the filter chips, taken from the tenant's OWN data.
 *
 * Deliberately not a platform-wide list: those carry 989 l2 categories and 210
 * cities, so a picker built from them offers Glenmark 983 categories that
 * return an empty table.
 *
 * All three dimensions come back from ONE request and share one cache entry —
 * the three lists are three GROUP BYs over the same scan, so fetching them
 * separately would triple the cost to answer the same question. The probe
 * window is pinned to last_7_days regardless of the window the page is
 * showing: the option LIST barely moves day to day, and pinning it keeps one
 * cache entry per tenant instead of one per window the user tries.
 */
function useAllOptions(
  customerName: string,
  enabled: boolean,
): UseQueryResult<Record<Dim, Option[]>, Error> {
  return useQuery({
    queryKey: ['filter-options', customerName],
    enabled,
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const res = await postOverlay<Record<string, unknown>>(
        '/v1/availability/options',
        { customer_name: customerName, window: { token: 'last_7_days' } },
        signal,
      );
      const out: Record<Dim, Option[]> = { category: [], city: [], sku: [] };
      const seen: Record<Dim, Set<string>> = { category: new Set(), city: new Set(), sku: new Set() };
      for (const row of res.rows) {
        const dim = str(row['dim']) as Dim;
        const value = str(row['value']);
        if (!value || !(dim in out) || seen[dim].has(value)) continue;
        seen[dim].add(value);
        out[dim].push({ value, label: value, weight: num(row['weight']) ?? 0 });
      }
      return out;
    },
  });
}

export function useFilterOptions(
  dim: Dim,
  customerName: string,
  enabled: boolean,
): UseQueryResult<Option[], Error> {
  const all = useAllOptions(customerName, enabled);
  // Re-typed rather than re-fetched: every picker on the page reads its slice
  // out of the same cached response.
  return {
    ...all,
    data: all.data ? all.data[dim] : undefined,
  } as UseQueryResult<Option[], Error>;
}
